// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Optionen → Mitglieder (nur Admins): Konten anlegen und verwalten.
// Jede Zeile klappt auf: Name ändern, Benutzernamen setzen, Rolle wechseln,
// Katalog-Recht vergeben, neues Startpasswort, Konto löschen. Löschen ist
// zweistufig und nie fürs eigene Konto möglich (Migration 24) — ein Fehlklick
// soll weder die Trainingshistorie einer Person noch den eigenen Zugang zur
// Verwaltung mitreißen.
import { useEffect, useState } from "react"
import { pb } from "@/lib/pb"
import {
  aendereMitglied, benutzernameAus, einladungsText, ladeMitglieder, legeMitgliedAn,
  loescheMitglied, passwortVorschlag, setzeStartpasswort, zugangsText, type Mitglied,
} from "@/lib/mitglieder"
import { toast } from "@/lib/toast"

const NETZ = "Dafür brauchst du Internet – bitte später erneut versuchen"

export default function MitgliederVerwaltung() {
  const [liste, setListe] = useState<Mitglied[]>([])
  const [neuOffen, setNeuOffen] = useState(false)
  const [offenId, setOffenId] = useState<string | null>(null)
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [benutzer, setBenutzer] = useState("")
  const [benutzerHand, setBenutzerHand] = useState(false) // von Hand geändert?
  const [pw, setPw] = useState(passwortVorschlag())
  const [admin, setAdmin] = useState(false)
  const [kurator, setKurator] = useState(true)
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState("")
  const [zugang, setZugang] = useState<{ benutzer: string; pw: string; name: string } | null>(null)
  const [kopiert, setKopiert] = useState<"einladung" | "daten" | null>(null)
  const [loeschId, setLoeschId] = useState<string | null>(null)

  const meineId = pb.authStore.record?.id ?? ""
  const laden = () => { void ladeMitglieder().then(setListe).catch(() => {}) }
  useEffect(laden, [])

  // Benutzername folgt dem Namen, bis der Admin ihn selbst anfasst
  function nameGeaendert(v: string) {
    setName(v)
    if (!benutzerHand) setBenutzer(benutzernameAus(v, liste.map((m) => m.username)))
  }

  async function anlegen(e: React.FormEvent) {
    e.preventDefault()
    setFehler("")
    setBusy(true)
    try {
      await legeMitgliedAn({ username: benutzer, email, displayName: name, passwort: pw, admin, kurator })
      setZugang({ benutzer: benutzer.trim().toLowerCase(), pw, name })
      setEmail(""); setName(""); setBenutzer(""); setBenutzerHand(false)
      setAdmin(false); setKurator(true)
      setPw(passwortVorschlag())
      setNeuOffen(false)
      laden()
      toast("Konto angelegt ✓")
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : ""
      setFehler(msg.includes("schwach") ? msg : "Das hat nicht geklappt. Ist der Benutzername schon vergeben?")
    } finally {
      setBusy(false)
    }
  }

  async function aendern(m: Mitglied, patch: Parameters<typeof aendereMitglied>[1]) {
    try {
      await aendereMitglied(m.id, patch)
      laden()
      toast("Gespeichert ✓")
    } catch {
      toast(NETZ)
    }
  }

  /** In die Zwischenablage legen. Fällt das Gerät aus (alter Browser, kein
   *  HTTPS), bleibt der Text sichtbar auf dem Bildschirm — abtippen geht immer. */
  async function kopieren(text: string, welcher: "einladung" | "daten") {
    try {
      await navigator.clipboard.writeText(text)
      setKopiert(welcher)
      window.setTimeout(() => setKopiert(null), 2000)
    } catch {
      toast("Kopieren geht hier nicht – bitte von Hand übertragen")
    }
  }

  async function loeschen(m: Mitglied) {
    try {
      await loescheMitglied(m.id)
      setLoeschId(null)
      setOffenId(null)
      laden()
      toast("Konto gelöscht")
    } catch {
      toast(NETZ)
    }
  }

  async function neuesStartpasswort(m: Mitglied) {
    const neu = passwortVorschlag()
    try {
      await setzeStartpasswort(m.id, neu)
      setZugang({ benutzer: m.username || m.email, pw: neu, name: m.displayName })
      laden()
    } catch {
      toast(NETZ)
    }
  }

  return (
    <>
      <h2>Mitglieder</h2>

      {zugang && (
        /* Einmalige Anzeige zum Weitergeben — danach kennt es nur die Person */
        <div className="card mitgl-zugang">
          <b>Zugangsdaten weitergeben:</b>
          <p>Adresse: <code>{window.location.origin}</code></p>
          <p>Benutzername: <code>{zugang.benutzer}</code></p>
          <p>Startpasswort: <code>{zugang.pw}</code></p>
          <p className="hint" style={{ margin: "6px 0 0" }}>
            Beim nächsten Anmelden verlangt die App ein eigenes Passwort. Danach ist dieses hier wertlos.
          </p>
          {/* Zwei Wege: die fertige Einladung für Leute, die noch nichts davon
              wissen — und die nackten Daten, wenn schon geredet wurde. */}
          <button type="button" className="save pressable" style={{ marginTop: 10 }}
            onClick={() => void kopieren(einladungsText(zugang.benutzer, zugang.pw, zugang.name), "einladung")}>
            {kopiert === "einladung" ? "Kopiert ✓" : "Einladung zum Verschicken kopieren"}
          </button>
          <button type="button" className="btn-secondary pressable" style={{ marginTop: 8, width: "100%" }}
            onClick={() => void kopieren(zugangsText(zugang.benutzer, zugang.pw), "daten")}>
            {kopiert === "daten" ? "Kopiert ✓" : "Nur die Zugangsdaten"}
          </button>
          <button type="button" className="btn-secondary" style={{ marginTop: 8, width: "100%" }} onClick={() => setZugang(null)}>
            Verstanden
          </button>
        </div>
      )}

      <div className="card">
        <div className="mitgl-liste">
          {liste.map((m) => {
            const offen = offenId === m.id
            const ichSelbst = m.id === meineId
            return (
              <div className={offen ? "mitgl offen" : "mitgl"} key={m.id}>
                <button type="button" className="mitgl-head" aria-expanded={offen}
                  onClick={() => setOffenId(offen ? null : m.id)}>
                  <span className="mitgl-name">
                    {m.displayName || m.email}{ichSelbst && <i> (du)</i>}
                  </span>
                  <span className="mitgl-mail">{m.username || m.email || "ohne Benutzernamen"}</span>
                  <span className="mitgl-tags">
                    {m.role === "admin" && <span className="tag tag-admin">Verwaltung</span>}
                    {m.may_curate && <span className="tag tag-kurator">Katalog</span>}
                    {m.must_change_password && <span className="tag tag-neu">Startpasswort offen</span>}
                  </span>
                </button>

                {offen && (
                  <div className="mitgl-body">
                    <label htmlFor={`mg-n-${m.id}`}>Name</label>
                    <input id={`mg-n-${m.id}`} type="text" maxLength={100} defaultValue={m.displayName}
                      onBlur={(e) => {
                        const v = e.target.value.trim()
                        if (v !== m.displayName) void aendern(m, { displayName: v })
                      }} />

                    <label htmlFor={`mg-u-${m.id}`} style={{ marginTop: 10, display: "block" }}>Benutzername</label>
                    <input id={`mg-u-${m.id}`} type="text" maxLength={40} defaultValue={m.username}
                      autoCapitalize="none" spellCheck={false}
                      onBlur={(e) => {
                        const v = benutzernameAus(e.target.value)
                        e.target.value = v
                        if (v && v !== m.username) void aendern(m, { username: v })
                      }} />
                    {m.email && <p className="hint" style={{ margin: "6px 0 0" }}>E-Mail: {m.email}</p>}

                    <label className="opt-check" style={{ marginTop: 12 }}>
                      <input type="checkbox" checked={m.may_curate} disabled={m.role === "admin"}
                        onChange={(e) => void aendern(m, { may_curate: e.target.checked })} />
                      Darf Videos und Texte im gemeinsamen Übungskatalog pflegen
                      {m.role === "admin" && <i> – für die Verwaltung ohnehin erlaubt</i>}
                    </label>

                    <label className="opt-check">
                      <input type="checkbox" checked={m.role === "admin"} disabled={ichSelbst}
                        onChange={(e) => void aendern(m, { role: e.target.checked ? "admin" : "user" })} />
                      Verwaltung: darf Mitglieder anlegen und ändern
                      {ichSelbst && <i> – dein eigenes Konto kannst du hier nicht herabstufen</i>}
                    </label>

                    {!ichSelbst && (
                      <button type="button" className="btn-secondary pressable" style={{ width: "100%", marginTop: 12 }}
                        onClick={() => void neuesStartpasswort(m)}>
                        Neues Startpasswort vergeben
                      </button>
                    )}

                    {/* Löschen ist endgültig und nimmt alles mit — deshalb
                        zweistufig und ohne window.confirm (wird in der App
                        verschluckt, §13.6). Das eigene Konto lässt die Regel
                        ohnehin nicht zu (Migration 24). */}
                    {!ichSelbst && (loeschId === m.id ? (
                      <div className="loesch-frage">
                        <p>
                          <b>{m.displayName || m.username}</b> und <b>alle Trainingsdaten</b> dieser Person
                          endgültig löschen? Eigene Pläne, Körpergewichte und Feed-Beiträge gehen mit.
                          Das lässt sich nicht rückgängig machen.
                        </p>
                        <div>
                          <button type="button" className="btn-secondary" onClick={() => setLoeschId(null)}>Abbrechen</button>
                          <button type="button" className="loesch-frage-ja" onClick={() => void loeschen(m)}>
                            Endgültig löschen
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" className="btn-secondary btn-danger pressable"
                        style={{ width: "100%", marginTop: 8 }} onClick={() => setLoeschId(m.id)}>
                        Konto löschen
                      </button>
                    ))}

                    {ichSelbst && (
                      <p className="hint" style={{ margin: "8px 0 0" }}>
                        Dein eigenes Konto kannst du hier nicht löschen – sonst käme niemand mehr
                        an die Mitgliederverwaltung.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {neuOffen ? (
          <form className="mitgl-form" onSubmit={anlegen}>
            <label htmlFor="mg-name">Name</label>
            <input id="mg-name" type="text" maxLength={100} value={name} placeholder="Vorname genügt"
              onChange={(e) => nameGeaendert(e.target.value)} autoFocus />

            <label htmlFor="mg-user">Benutzername zum Anmelden</label>
            <input id="mg-user" type="text" required maxLength={40} autoComplete="off"
              autoCapitalize="none" spellCheck={false} value={benutzer}
              onChange={(e) => { setBenutzerHand(true); setBenutzer(benutzernameAus(e.target.value)) }} />

            <label htmlFor="mg-mail">E-Mail-Adresse <i>(freiwillig)</i></label>
            <input id="mg-mail" type="email" autoComplete="off" value={email} placeholder="nur fürs Passwort-Zurücksetzen"
              onChange={(e) => setEmail(e.target.value)} />

            <label htmlFor="mg-pw">Startpasswort</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input id="mg-pw" type="text" required value={pw} onChange={(e) => setPw(e.target.value)} />
              <button type="button" className="btn-secondary pressable" style={{ whiteSpace: "nowrap" }}
                onClick={() => setPw(passwortVorschlag())}>Neuer Vorschlag</button>
            </div>
            <p className="hint" style={{ margin: "6px 0 0" }}>
              Mind. 10 Zeichen mit Ziffer und Sonderzeichen. Die Person ersetzt es beim ersten Anmelden.
            </p>

            <label className="opt-check" style={{ marginTop: 12 }}>
              <input type="checkbox" checked={kurator} onChange={(e) => setKurator(e.target.checked)} />
              Darf Videos und Texte im gemeinsamen Übungskatalog pflegen
            </label>
            <label className="opt-check">
              <input type="checkbox" checked={admin} onChange={(e) => setAdmin(e.target.checked)} />
              Verwaltung: darf Mitglieder anlegen und ändern
            </label>

            {fehler && <p className="form-error" role="alert">{fehler}</p>}

            <div className="uebform-actions">
              <button type="button" className="btn-secondary pressable" onClick={() => setNeuOffen(false)}>Abbrechen</button>
              <button type="submit" className="save pressable" style={{ marginTop: 0 }} disabled={busy || !benutzer.trim()}>
                {busy ? "Legt an…" : "Konto anlegen"}
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="picker-neu" style={{ marginTop: 12 }} onClick={() => setNeuOffen(true)}>
            + Mitglied hinzufügen
          </button>
        )}
      </div>
    </>
  )
}
